import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { formatAttachmentFileSize, normalizeHrAttachments } from '../../utils/attachmentUpload';
import { formatUsDateFromIso, formatUsDateFromDateTime, formatUsDateTimeFromIso } from '../../utils/usDateFormat';
import { formatEmployeeNameWithNo } from '../../utils/employeeDisplay';
import { canApproveHrRequest, canPrintHrRequest } from '../../utils/hrRequestApproval';

const DetailedLeavePopup = ({
  leave,
  onClose,
  userProfile,
  currentView,
  isDeletedView,
  handleApproveLeave,
  handleDeleteRequest,
  canDelete,
  getLeaveTypeColor,
}) => {
  const popupRef = useRef(null);
  const attachments = useMemo(() => normalizeHrAttachments(leave?.attachments), [leave?.attachments]);
  const requestId = leave?.id || leave?.hr_request_id;

  // Handle Escape key to close popup
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  // Handle click outside to close popup
  const handleOverlayClick = useCallback(
    (event) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  // Focus popup when it opens
  useEffect(() => {
    if (leave && popupRef.current) {
      popupRef.current.focus();
    }
  }, [leave]);

  useEffect(() => {
    const cleanup = () => document.body.classList.remove('hr-request-printing');
    window.addEventListener('afterprint', cleanup);
    return () => window.removeEventListener('afterprint', cleanup);
  }, []);

  const handlePrint = useCallback(() => {
    document.body.classList.add('hr-request-printing');
    window.print();
  }, []);

  if (!leave) return null;

  const canApprove = !isDeletedView && canApproveHrRequest(leave, userProfile, currentView);
  const canPrint = !isDeletedView && canPrintHrRequest(leave);

  const formatDeletedMeta = (value) => {
    if (value == null || value === '') return '—';
    return formatUsDateTimeFromIso(value) || formatUsDateFromDateTime(value) || formatUsDateFromIso(value) || '—';
  };

  return (
    <div
      className="hr-request-print-overlay fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto overscroll-contain py-4 px-2 sm:items-center sm:py-6"
      onClick={handleOverlayClick}
    >
      <div
        ref={popupRef}
        id="hr-request-printable"
        className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4 max-h-[min(90vh,calc(100dvh-2rem))] flex flex-col min-w-0 my-auto overflow-hidden"
        role="dialog"
        aria-labelledby="popup-title"
        aria-describedby="popup-description"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className="mb-4 flex justify-end">
          <span
            className={`font-medium px-3 py-1 rounded-full text-sm
              ${leave?.status === 'Request Submitted' ? 'bg-gray-100 text-gray-800' : ''}
              ${leave?.status === 'Manager Approved' ? 'bg-green-100 text-green-800' : ''}
              ${leave?.status === 'Payroll Manager Approved' ? 'bg-green-100 text-green-800' : ''}
              ${leave?.status === 'HR Manager Approved' ? 'bg-green-100 text-green-800' : ''}
              ${leave?.status === 'Manager Rejected' ? 'bg-red-100 text-red-800' : ''}
              ${leave?.status === 'Payroll Manager Rejected' ? 'bg-red-100 text-red-800' : ''}
              ${leave?.status === 'HR Manager Rejected' ? 'bg-red-100 text-red-800' : ''}
            `}
          >
            {leave.status}
          </span>
        </div>
        <div
          id="popup-description"
          className="space-y-2 text-sm text-gray-600 mb-4 min-h-0 flex-1 overflow-y-auto pr-1"
        >
          {leave.tracking_id ? (
            <p><strong>Request ID:</strong> {leave.tracking_id}</p>
          ) : null}
          <p><strong>Employee:</strong> {formatEmployeeNameWithNo(leave.employee_name, leave.employee_no)}</p>
          <p><strong>Leave Category:</strong> {leave.category_name || 'Unknown'}</p>
          {leave.leave_type_name && (
            <p><strong>Leave Type:</strong> {leave.leave_type_name || 'Unknown'}</p>
          )}
          {leave.start_date && leave.end_date ? (
            <p>
              <strong>Period:</strong>{' '}
              {formatUsDateFromIso(leave.start_date)} - {formatUsDateFromIso(leave.end_date)}
            </p>
          ) : null}
          {leave.time_from && leave.time_to ? (
            <>
              <p>
                <strong>Date:</strong>{' '}
                {(() => {
                  const dFrom = formatUsDateFromDateTime(leave.time_from);
                  const dTo = formatUsDateFromDateTime(leave.time_to);
                  return dFrom === dTo ? dFrom : `${dFrom} - ${dTo}`;
                })()}
              </p>
              <p>
                <strong>Time:</strong>{' '}
                {new Date(leave.time_from).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })} -{' '}
                {new Date(leave.time_to).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })}
              </p>
            </>
          ) : null}
          {leave.days_requested && (
            <p><strong>Days:</strong> {leave.days_requested}</p>
          )}
          {leave.resume_remarks && (
            <p><strong>Resume Remarks:</strong> {leave.resume_remarks}</p>
          )}
          <p><strong>Reason:</strong> {leave.reason}</p>
          <p><strong>Applied:</strong> {(() => {
            const v = leave.applied_date;
            if (v == null || v === '') return '—';
            if (currentView === 'my_team_requests') {
              return formatUsDateTimeFromIso(v) || formatUsDateFromDateTime(v) || formatUsDateFromIso(v) || '—';
            }
            return formatUsDateFromDateTime(v) || formatUsDateFromIso(v) || '—';
          })()}</p>
          {leave.approved_by && (
            <p><strong>Processed by:</strong> {leave.approved_by}</p>
          )}
          {leave.manager_comments && (
            <p><strong>Manager Comments:</strong> {leave.manager_comments}</p>
          )}
          {isDeletedView && (
            <>
              <p><strong>Deleted by:</strong> {leave.deleted_by_name || leave.deleted_by_email || '—'}</p>
              <p><strong>Deleted at:</strong> {formatDeletedMeta(leave.deleted_at)}</p>
              <p><strong>Expires at:</strong> {formatDeletedMeta(leave.expires_at)}</p>
            </>
          )}
          <div className="hr-request-no-print pt-3 mt-2 border-t border-gray-200">
            <p className="font-semibold text-gray-800 mb-2 text-base">Attachments</p>
            {attachments.length === 0 ? (
              <p className="text-gray-500 text-sm">No files attached to this request.</p>
            ) : (
              <ul className="space-y-2">
                {attachments.map((att) => (
                  <li
                    key={att.id}
                    className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-gray-800 font-medium truncate" title={att.file_name}>
                        {att.file_name}
                      </p>
                      <p className="text-xs text-gray-500">{formatAttachmentFileSize(att.size)}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                      <a
                        href={att.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-3 py-1.5 rounded-md bg-[#674936] text-white text-xs font-semibold hover:bg-[#503220] touch-manipulation"
                      >
                        View
                      </a>
                      <a
                        href={att.file_url}
                        download={att.file_name}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-3 py-1.5 rounded-md border border-[#674936] text-[#674936] text-xs font-semibold hover:bg-[#f5f0eb] touch-manipulation"
                      >
                        Download
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="hr-request-no-print flex justify-end items-center gap-2">
          {canApprove && (
            <>
              <button
                type="button"
                onClick={() => {
                  const comments = prompt('Add comments (optional):');
                  if (comments === null) return;
                  handleApproveLeave(requestId, 'approve', comments);
                }}
                className="bg-[#e6fffa] text-[#38a169] px-4 py-2 rounded-lg hover:bg-[#ccfff2] transition-colors duration-300 font-semibold"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={() => {
                  const comments = prompt('Add rejection reason:');
                  if (comments === null) return;
                  handleApproveLeave(requestId, 'reject', comments);
                }}
                className="bg-[#fed7d7] text-[#e53e3e] px-4 py-2 rounded-lg hover:bg-[#ffb3b3] transition-colors duration-300 font-semibold"
              >
                Reject
              </button>
            </>
          )}
          {canPrint && (
            <button
              type="button"
              onClick={handlePrint}
              className="border border-[#674936] text-[#674936] px-4 py-2 rounded-lg hover:bg-[#f5f0eb] transition-colors duration-300 font-semibold"
            >
              Print
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => handleDeleteRequest(leave)}
              className="bg-[#fed7d7] text-[#e53e3e] px-4 py-2 rounded-lg hover:bg-[#ffb3b3] transition-colors duration-300 font-semibold"
            >
              Delete
            </button>
          )}
          <button
            onClick={onClose}
            className="bg-[#674936] text-white px-4 py-2 rounded-lg hover:bg-[#8B5F3D] transition-colors duration-300 font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetailedLeavePopup;